/* eslint-disable consistent-return */
import { useEffect, useRef } from 'react'

import { makeVar, useReactiveVar } from '@apollo/client'
import { useEffectOnce } from 'react-use'

import {
  getDarkModeQuery,
  checkSupportDarkMode,
  THEME_COLOR_META,
  THEME_COLOR_META_ID,
  MYPLACE_DARK_MODE_CLASSNAMES,
} from '@web/components/Document/DarkModeTag'

export const darkModeVar = makeVar<boolean>(false)
export const darkModeLoadingVar = makeVar<boolean>(true)

const changeDarkMode = (dark: boolean) => {
  darkModeVar(dark)
  darkModeLoadingVar(false)
  document?.body.classList[dark ? 'add' : 'remove'](...MYPLACE_DARK_MODE_CLASSNAMES)
  document
    .getElementById(THEME_COLOR_META_ID)
    ?.setAttribute('content', dark ? THEME_COLOR_META.dark : THEME_COLOR_META.light)
}

export const useDisableDarkMode = () => {
  const darkMode = useReactiveVar(darkModeVar)
  const prevDarkMode = useRef(darkMode)

  useEffect(() => {
    const disableDarkMode = () => changeDarkMode(false)

    if (darkMode) {
      disableDarkMode()
      prevDarkMode.current = darkMode
    }
  }, [darkMode])

  useEffectOnce(() => {
    const revertDarkMode = () => changeDarkMode(prevDarkMode.current)

    return revertDarkMode
  })
}

export const naverAppearanceVar = makeVar<'system' | 'light'>('system')

export const useGlobalDetermineDarkMode = () => {
  useEffectOnce(() => {
    const appearance = checkSupportDarkMode() ? 'system' : 'light'
    naverAppearanceVar(appearance)
  })

  useEffect(() => {
    const doesSupportDarkMode = checkSupportDarkMode()
    changeDarkMode(doesSupportDarkMode && !!getDarkModeQuery()?.matches)

    const darkMediaQuery = getDarkModeQuery()
    const handleDarkModeChange = (ev: MediaQueryListEvent) => {
      changeDarkMode(doesSupportDarkMode && ev.matches)
    }

    // addEventListener를 지원하는 경우
    if ('addEventListener' in darkMediaQuery) {
      darkMediaQuery.addEventListener('change', handleDarkModeChange)
      return () => {
        darkMediaQuery.removeEventListener('change', handleDarkModeChange)
      }
    }
  }, [])
}

export const useColorScheme = () => {
  const dark = useReactiveVar(darkModeVar)
  return { dark }
}
